import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  TeamOutlined,
  RiseOutlined,
  CalendarOutlined,
  SolutionOutlined,
  CheckSquareOutlined,
  FileTextOutlined,
  BellOutlined,
  ExclamationCircleOutlined,
  RightOutlined,
  ArrowUpOutlined,
  ArrowDownOutlined,
  DownOutlined,
} from '@ant-design/icons';
import { Card, Tag, Dropdown, Empty, message } from 'antd';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import AdminLayout from '@/components/layout/AdminLayout';
import { DashboardAnnouncement, DashboardStat, DashboardTodo, DashboardTrendPoint, getHomeDashboard } from '@/api/dashboard';

const quickEntries = [
  {
    key: 'member-review',
    title: '入会审核',
    desc: '处理待审核入会申请',
    icon: <TeamOutlined className="text-xl" />,
    iconBg: 'bg-blue-50',
    iconColor: 'text-blue-500',
  },
  {
    key: 'business-list',
    title: '商机发布',
    desc: '发布与管理商机信息',
    icon: <RiseOutlined className="text-xl" />,
    iconBg: 'bg-emerald-50',
    iconColor: 'text-emerald-500',
  },
  {
    key: 'activity-list',
    title: '活动发布',
    desc: '创建与发布商会活动',
    icon: <CalendarOutlined className="text-xl" />,
    iconBg: 'bg-amber-50',
    iconColor: 'text-amber-500',
  },
  {
    key: 'enterprise-list',
    title: '会员名录',
    desc: '查看会员企业名录',
    icon: <SolutionOutlined className="text-xl" />,
    iconBg: 'bg-purple-50',
    iconColor: 'text-purple-500',
  },
  {
    key: 'merchant-writeoff',
    title: '核销记录',
    desc: '查看会员核销记录',
    icon: <CheckSquareOutlined className="text-xl" />,
    iconBg: 'bg-cyan-50',
    iconColor: 'text-cyan-500',
  },
];

const rangeMap: Record<string, string> = {
  今日: 'today',
  本周: 'week',
  本月: 'month',
};

export default function DashboardPage() {
  const navigate = useNavigate();
  const [timeRange, setTimeRange] = useState('今日');
  const [statsData, setStatsData] = useState<DashboardStat[]>([]);
  const [chartData, setChartData] = useState<DashboardTrendPoint[]>([]);
  const [announcements, setAnnouncements] = useState<DashboardAnnouncement[]>([]);
  const [todoItems, setTodoItems] = useState<DashboardTodo[]>([]);

  useEffect(() => {
    getHomeDashboard(rangeMap[timeRange] || 'today')
      .then((data) => {
        setStatsData(data?.stats || []);
        setChartData(data?.trend || []);
        setAnnouncements(data?.announcements || []);
        setTodoItems(data?.todos || []);
      })
      .catch((error) => {
        message.error(error.message || '获取首页数据失败');
        setStatsData([]);
        setChartData([]);
        setAnnouncements([]);
        setTodoItems([]);
      });
  }, [timeRange]);

  const handleQuickEntry = (key: string) => {
    const pathMap: Record<string, string> = {
      'member-review': '/member/review',
      'business-list': '/business/list',
      'activity-list': '/activity/list',
      'enterprise-list': '/enterprise/list',
      'merchant-writeoff': '/merchant/writeoff',
    };
    const path = pathMap[key];
    if (path) {
      navigate(path);
    }
  };

  const timeRangeItems = [
    { key: '今日', label: '今日' },
    { key: '本周', label: '本周' },
    { key: '本月', label: '本月' },
  ];

  return (
    <AdminLayout pageTitle="首页" activeKey="dashboard">
      {/* 顶部欢迎 Banner */}
      <div className="relative overflow-hidden rounded-xl mb-6 h-[320px]">
        <img
          src="https://static.readdy.ai/image/f4e14b58753752861061999bf16a9230/b5df0303c61a729383440cb438532789.png"
          alt="banner"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-white/80 via-white/50 to-transparent"></div>
        <div className="relative z-10 flex items-center h-full px-8">
          <div className="flex-1">
            <h1 className="text-3xl font-bold text-gray-800 mb-3">
              欢迎使用 <span className="text-blue-500">儋州市那大镇商会数字化平台</span>
            </h1>
            <p className="text-sm text-gray-500 mb-4 tracking-widest">
              高效 · 便捷 · 智能 · 安全
            </p>
            <p className="text-sm text-gray-600">
              您好，管理员，祝您工作顺利，今天又是充实的一天！
            </p>
          </div>
        </div>
      </div>

      {/* 快捷入口 */}
      <div className="mb-6">
        <h3 className="text-sm font-medium text-gray-800 mb-4">快捷入口</h3>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
          {quickEntries.map((item) => (
            <div
              key={item.key}
              onClick={() => handleQuickEntry(item.key)}
              className="flex items-center gap-3 p-4 bg-white rounded-lg border border-gray-100 cursor-pointer hover:border-blue-200 hover:shadow-sm transition-all"
            >
              <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${item.iconBg}`}>
                <span className={item.iconColor}>{item.icon}</span>
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium text-gray-800">{item.title}</div>
                <div className="text-xs text-gray-400 mt-0.5 truncate">{item.desc}</div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* 底部三栏 */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        {/* 左侧：数据概览 + 图表 */}
        <div className="lg:col-span-5">
          <Card
            title={
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-gray-800">数据概览</span>
                <Dropdown
                  menu={{
                    items: timeRangeItems.map((item) => ({
                      key: item.key,
                      label: item.label,
                      onClick: () => setTimeRange(item.label),
                    })),
                  }}
                  placement="bottomRight"
                >
                  <span className="flex items-center gap-1 text-xs text-gray-500 cursor-pointer hover:text-blue-500">
                    {timeRange}
                    <DownOutlined className="text-xs" />
                  </span>
                </Dropdown>
              </div>
            }
            bodyStyle={{ padding: '16px' }}
            className="h-full"
          >
            {/* 统计卡 */}
            <div className="grid grid-cols-2 gap-4 mb-6">
              {statsData.map((stat, index) => (
                <div key={stat.label || index} className="flex flex-col">
                  <span className="text-xs text-gray-500 mb-1">{stat.label}</span>
                  <span className="text-xl font-bold text-gray-800 mb-1">{stat.value}</span>
                  <div className="flex items-center gap-1 text-xs">
                    <span className="text-gray-400">较昨日</span>
                    {stat.up ? (
                      <span className="text-emerald-500 flex items-center gap-0.5">
                        <ArrowUpOutlined className="text-xs" />
                        {stat.change}%
                      </span>
                    ) : (
                      <span className="text-red-500 flex items-center gap-0.5">
                        <ArrowDownOutlined className="text-xs" />
                        {stat.change}%
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>

            {/* 折线图 */}
            <div className="h-[200px]">
              {chartData.length ? (
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis
                    dataKey="time"
                    tick={{ fontSize: 11, fill: '#999' }}
                    axisLine={{ stroke: '#e8e8e8' }}
                    tickLine={false}
                  />
                  <YAxis
                    tick={{ fontSize: 11, fill: '#999' }}
                    axisLine={false}
                    tickLine={false}
                  />
                  <Tooltip
                    contentStyle={{
                      borderRadius: 8,
                      border: 'none',
                      boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                      fontSize: 12,
                    }}
                  />
                  <Line
                    type="monotone"
                    dataKey="value"
                    stroke="#1890ff"
                    strokeWidth={2}
                    dot={false}
                    activeDot={{ r: 4, fill: '#1890ff', stroke: '#fff', strokeWidth: 2 }}
                  />
                  </LineChart>
                </ResponsiveContainer>
              ) : <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="暂无趋势数据" />}
            </div>
          </Card>
        </div>

        {/* 中间：系统公告 */}
        <div className="lg:col-span-4">
          <Card
            title={
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-gray-800">系统公告</span>
                <span className="flex items-center text-xs text-gray-400 cursor-pointer hover:text-blue-500">
                  查看更多 <RightOutlined className="text-xs ml-0.5" />
                </span>
              </div>
            }
            bodyStyle={{ padding: 0 }}
            className="h-full"
          >
            <div className="px-4 py-3">
              {announcements.length === 0 && <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="暂无公告" />}
              {announcements.map((item, index) => (
                <div
                  key={item.id}
                  className={`flex items-center justify-between py-3 ${
                    index !== announcements.length - 1 ? 'border-b border-gray-50' : ''
                  }`}
                >
                  <div className="flex items-center gap-2 flex-1 min-w-0">
                    {item.isTop && (
                      <Tag color="blue" className="text-xs px-1 py-0 m-0 flex-shrink-0">
                        置顶
                      </Tag>
                    )}
                    <span className="text-sm text-gray-700 truncate cursor-pointer hover:text-blue-500">
                      {item.title}
                    </span>
                  </div>
                  <span className="text-xs text-gray-400 flex-shrink-0 ml-2">{item.date}</span>
                </div>
              ))}
            </div>
          </Card>
        </div>

        {/* 右侧：待办事项 */}
        <div className="lg:col-span-3">
          <Card
            title={
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-gray-800">待办事项</span>
                <span className="flex items-center text-xs text-gray-400 cursor-pointer hover:text-blue-500">
                  查看更多 <RightOutlined className="text-xs ml-0.5" />
                </span>
              </div>
            }
            bodyStyle={{ padding: 0 }}
            className="h-full"
          >
            <div className="px-4 py-3">
              {todoItems.length === 0 && <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="暂无待办" />}
              {todoItems.map((item, index) => (
                <div
                  key={item.id}
                  className={`flex items-center justify-between py-3 ${
                    index !== todoItems.length - 1 ? 'border-b border-gray-50' : ''
                  }`}
                >
                  <div className="flex items-center gap-3 flex-1 min-w-0">
                    <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${item.iconBg}`}>
                      <span className={item.iconColor}>
                        {item.type === 'review' && <FileTextOutlined className="text-sm" />}
                        {item.type === 'task' && <ExclamationCircleOutlined className="text-sm" />}
                        {item.type === 'todo' && <BellOutlined className="text-sm" />}
                      </span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm text-gray-700 truncate">{item.title}</div>
                      <div className="text-xs text-gray-400 mt-0.5">{item.desc}</div>
                    </div>
                  </div>
                  <span className="text-xs text-gray-400 flex-shrink-0 ml-2">{item.time}</span>
                </div>
              ))}
            </div>
          </Card>
        </div>
      </div>
    </AdminLayout>
  );
}
